import React, { useState } from "react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../ui/accordion"
import { ChevronDown } from "lucide-react"

const HelpdeskFAQ = () => {
  const [openSection, setOpenSection] = useState("application")
  const [openQuestion, setOpenQuestion] = useState(null)

  const faqSections = [
    {
      title: "Application Process",
      items: [
        {
          question: "How do I apply for a position?",
          answer:
            "To apply, visit our careers page, find a position that matches your skills and interests, and click the 'Apply' button. You'll need to submit your resume and answer a few questions about your experience and qualifications."
        },
        {
          question: "What happens after I submit my application?",
          answer:
            "Our hiring team reviews all applications. If your qualifications match our needs, you'll be contacted for an initial screening call. This is usually followed by technical assessments and interviews with the team."
        },
        {
          question: "Can I apply for multiple positions?",
          answer:
            "Yes, you can apply for multiple positions that match your skills and experience. However, we recommend focusing on roles that best align with your career goals and expertise."
        },
        {
          question: "How long does the application process take?",
          answer:
            "The timeline varies depending on the position and volume of applications, but we strive to complete the process within 2-4 weeks from the initial application to the final decision."
        }
      ]
    },
    {
      title: "Technical Support",
      items: [
        {
          question:
            "I'm having trouble accessing my account. What should I do?",
          answer:
            "First, try resetting your password using the 'Forgot Password' option. If that doesn't work, clear your browser cache and cookies, or try using a different browser. For persistent issues, contact our support team."
        },
        {
          question: "How do I update my contact information?",
          answer:
            "Log into your account, go to Profile Settings, and update your contact details in the Personal Information section. Don't forget to save your changes."
        },
        {
          question: "Is there a mobile app available?",
          answer:
            "Yes, our mobile app is available for both iOS and Android devices. You can download it from the App Store or Google Play Store by searching for 'Oye Creator'."
        },
        {
          question: "What browsers are supported?",
          answer:
            "Our platform works best on the latest versions of Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated for the best experience."
        }
      ]
    },
    {
      title: "Payments & Billing",
      items: [
        {
          question: "When will I receive my payment?",
          answer:
            "Payments are processed on the 15th of each month for the previous month's activity, provided you've reached the minimum payment threshold of $50. Funds typically appear in your account within 3-5 business days after processing."
        },
        {
          question: "What payment methods do you accept?",
          answer:
            "We accept major credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for most countries. Available payment options may vary by region."
        },
        {
          question: "How do I update my payment information?",
          answer:
            "Go to your Account Settings, select the 'Payment Methods' tab, and follow the instructions to add or update your payment details. For security reasons, you might need to verify your identity."
        },
        {
          question: "I haven't received my payment. What should I do?",
          answer:
            "First, check if you've reached the minimum payment threshold. Then, verify that your payment details are correct. If everything looks right and it's been more than 5 business days since the scheduled payment date, please contact our support team."
        }
      ]
    }
  ]

  const handleSectionClick = sectionKey => {
    if (openSection === sectionKey) {
      setOpenSection(null)
    } else {
      setOpenSection(sectionKey)
      setOpenQuestion(null) // Reset open question when changing sections
    }
  }

  return (
    <div className="space-y-6">
      {faqSections.map((section, index) => {
        const sectionKey = section.title.toLowerCase().split(" ")[0]

        return (
          <div
            key={sectionKey}
            className="bg-white rounded-lg shadow-sm border border-solid border-gray-100 overflow-hidden"
          >
            <div
              className="p-6 cursor-pointer flex justify-between items-center"
              onClick={() => handleSectionClick(sectionKey)}
            >
              <div className="text-xl font-semibold text-[#3D2A60]">
                {section.title}
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 transition-transform duration-300 ${
                  openSection === sectionKey ? "transform rotate-180" : ""
                }`}
              />
            </div>

            {openSection === sectionKey && (
              <div className="px-6 pb-6 pt-0 space-y-4">
                <Accordion
                  type="single"
                  collapsible
                  value={openQuestion || ""}
                  onValueChange={setOpenQuestion}
                >
                  {section.items.map((item, itemIndex) => (
                    <AccordionItem
                      key={`${sectionKey}-${itemIndex}`}
                      value={`${sectionKey}-${itemIndex}`}
                      className="border-t border-solid border-b-0 border-l-0 border-r-0 border-gray-100 pt-4 first:border-t-0 first:pt-0"
                    >
                      <AccordionTrigger className="font-medium text-gray-800 mb-2 hover:no-underline py-2">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default HelpdeskFAQ
